import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Tag
} from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Success, Error as ErrorMsg } from "../../../../commons/Messages/Messages";
import FilterToolbar from "../shared/FilterToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import { createAccount, errorText, updateAccount } from "./api";
import { ACCOUNT_TYPES, TAX_KINDS, TAX_KIND_LABELS, TYPE_COLORS, TYPE_LABELS } from "./labels";

const AccountModal = ({ open, account, onClose, onSaved }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const editing = Boolean(account?._id);
  const taxEditable = !editing || account.custom;

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue(
      account
        ? { ...account, tax: account.tax || { kind: "none", rate: 0 } }
        : {
            number: "",
            name: "",
            type: "expense",
            tax: { kind: "none", rate: 0 },
            euerLine: null,
            bwaLine: null,
            ustvaKz: null,
            active: true
          }
    );
  }, [open, account, form]);

  const submit = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    setSaving(true);
    try {
      if (editing) {
        const body = {
          name: values.name,
          active: values.active,
          euerLine: values.euerLine ?? null,
          bwaLine: values.bwaLine || null,
          ustvaKz: values.ustvaKz || null
        };
        if (taxEditable) body.tax = values.tax;
        await updateAccount(account._id, body);
        Success(t("Account updated"));
      } else {
        await createAccount({
          ...values,
          euerLine: values.euerLine ?? null,
          bwaLine: values.bwaLine || null,
          ustvaKz: values.ustvaKz || null
        });
        Success(t("Account created"));
      }
      onSaved();
    } catch (err) {
      ErrorMsg(errorText(err, t("Could not save the account")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      rootClassName="acct-module"
      open={open}
      onCancel={onClose}
      onOk={submit}
      okButtonProps={{ loading: saving }}
      title={
        editing ? t("Edit account {{number}}", { number: account.number }) : t("Add custom account")
      }
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Row gutter={[12, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="number"
              label={t("Number")}
              rules={[{ required: true, pattern: /^\d{4,5}$/, message: t("4 or 5 digits") }]}
            >
              <Input disabled={editing} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item
              name="name"
              label={t("Name")}
              rules={[{ required: true, message: t("Required") }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="type" label={t("Type")} rules={[{ required: true }]}>
          <Select
            disabled={editing}
            options={ACCOUNT_TYPES.filter(x => x !== "debitor" && x !== "kreditor").map(x => ({
              value: x,
              label: t(TYPE_LABELS[x])
            }))}
          />
        </Form.Item>
        <Row gutter={[12, 0]}>
          <Col xs={24} sm={16}>
            <Form.Item name={["tax", "kind"]} label={t("Tax kind")}>
              <Select
                disabled={!taxEditable}
                options={TAX_KINDS.map(k => ({ value: k, label: t(TAX_KIND_LABELS[k]) }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name={["tax", "rate"]} label={t("Tax rate")}>
              <InputNumber
                disabled={!taxEditable}
                min={0}
                max={19}
                style={{ width: "100%" }}
                addonAfter="%"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[12, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item name="euerLine" label={t("EÜR line")}>
              <InputNumber min={1} max={200} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item name="bwaLine" label={t("BWA line")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item name="ustvaKz" label={t("UStVA Kz")}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        {editing ? (
          <Form.Item name="active" label={t("Active")} valuePropName="checked">
            <Switch />
          </Form.Item>
        ) : null}
      </Form>
    </Modal>
  );
};

const AccountsTab = ({ accounts }) => {
  const { t } = useTranslation("accounting");
  const history = useHistory();
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const [type, setType] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState({ open: false, account: null });

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return accounts.accounts.filter(a => {
      if (!showInactive && !a.active) return false;
      if (type && a.type !== type) return false;
      if (needle && !a.number.startsWith(needle) && !a.name.toLowerCase().includes(needle))
        return false;
      return true;
    });
  }, [accounts.accounts, q, type, showInactive]);

  const columns = [
    {
      title: t("Number"),
      dataIndex: "number",
      width: 90,
      sorter: (a, b) => a.number.localeCompare(b.number),
      defaultSortOrder: "ascend",
      mobile: "title",
      // Cards: "4930 Bürobedarf" as the heading; the table keeps separate columns.
      render: (n, a) =>
        isMobile ? (
          <span className="acct-wrap">
            <span className="acct-num">{n}</span> {a.name}
          </span>
        ) : (
          <span className="acct-num">{n}</span>
        )
    },
    { title: t("Name"), dataIndex: "name", ellipsis: true, mobile: "hidden" },
    {
      title: t("Type"),
      dataIndex: "type",
      width: 130,
      mobile: "subtitle",
      render: v => <Tag color={TYPE_COLORS[v]}>{t(TYPE_LABELS[v] || v)}</Tag>
    },
    {
      title: t("Tax"),
      key: "tax",
      width: 120,
      render: (_, a) =>
        a.tax?.kind && a.tax.kind !== "none" ? `${a.tax.kind} ${a.tax.rate} %` : ""
    },
    { title: t("EÜR line"), dataIndex: "euerLine", width: 80, align: "end" },
    { title: t("BWA line"), dataIndex: "bwaLine", width: 80, mobile: "hidden" },
    { title: t("UStVA Kz"), dataIndex: "ustvaKz", width: 80, mobile: "hidden" },
    {
      title: t("Active"),
      dataIndex: "active",
      width: 70,
      render: v => (v ? <Tag color="success">{t("yes")}</Tag> : <Tag>{t("no")}</Tag>)
    },
    {
      key: "flags",
      width: 120,
      mobile: "subtitle",
      render: (_, a) => (
        <Space size={4} wrap>
          {a.custom ? <Tag color="purple">{t("Custom")}</Tag> : null}
          {a.linked?.kind === "customer" ? <Tag color="cyan">{t("Debitor")}</Tag> : null}
          {a.linked?.kind === "supplier" ? <Tag color="gold">{t("Kreditor")}</Tag> : null}
        </Space>
      )
    },
    {
      key: "edit",
      width: 50,
      render: (_, a) => (
        <Button
          type="text"
          icon={<EditOutlined />}
          aria-label={t("Edit")}
          onClick={e => {
            e.stopPropagation();
            setModal({ open: true, account: a });
          }}
        />
      )
    }
  ];

  return (
    <div>
      <FilterToolbar
        search={
          <Input.Search
            placeholder={t("Search number or name")}
            allowClear
            onChange={e => setQ(e.target.value)}
          />
        }
        activeCount={(type ? 1 : 0) + (showInactive ? 1 : 0)}
        filters={
          <>
            <Select
              allowClear
              placeholder={t("Type")}
              value={type}
              onChange={v => setType(v || null)}
              options={ACCOUNT_TYPES.map(x => ({ value: x, label: t(TYPE_LABELS[x]) }))}
            />
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Switch checked={showInactive} onChange={setShowInactive} />
              {t("Show inactive")}
            </label>
          </>
        }
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModal({ open: true, account: null })}
          >
            {t("Add custom account")}
          </Button>
        }
      />
      <ResponsiveTable
        rowKey="_id"
        size="small"
        loading={accounts.loading}
        columns={columns}
        dataSource={rows}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          showTotal: total => t("{{count}} accounts", { count: total })
        }}
        onRow={a => ({
          onClick: () => history.push(`/user/accounting/journal/accounts/${a.number}`),
          style: { cursor: "pointer" }
        })}
        scroll={{ x: 1000 }}
      />
      <AccountModal
        open={modal.open}
        account={modal.account}
        onClose={() => setModal({ open: false, account: null })}
        onSaved={() => {
          setModal({ open: false, account: null });
          accounts.reload();
        }}
      />
    </div>
  );
};

export default AccountsTab;
